import { useEffect, useState } from 'react';
import { LogIn, KeyRound, LogOut, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAppStore } from '@/stores/app-store';
import { api } from '@/api/client';
import { API_BASE, ENVIRONMENT_LABEL, COGNITO_CLIENT_ID } from '@/config';
import { cn } from '@/lib/utils';

/**
 * Auth controls: Cognito username/password login via the BFF, or raw
 * accessToken paste as a fallback. When a token is present, renders the
 * session status and a logout button instead.
 */
export function AuthPanel() {
  const { token, sessionExpired, setAuth, clearAuth } = useAppStore();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'token'>('login');
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      if (!token) { setTokenValid(false); return; }
      try {
        await api.get('/health');
        setTokenValid(true);
      } catch {
        setTokenValid(false);
      }
    };
    checkAuth();
  }, [token]);

  const handleSetToken = (val: string) => {
    setAuth(val, null);
    toast.success('Token saved');
  };

  const handleClearToken = () => {
    clearAuth();
    toast.success('Logged out');
  };

  const handleLogin = async () => {
    if (!username || !password) return;
    setLoginLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `Login failed (${res.status})`);
      }
      const accessToken = data.token;
      const refreshToken = data.refreshToken;
      if (!accessToken) {
        throw new Error('No token received from server');
      }
      setAuth(accessToken, refreshToken);
      setPassword('');
      setUsername('');
      toast.success('Signed in');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  if (token) {
    return (
      <div className="flex items-center justify-between">
        <span className={cn('text-xs font-medium', tokenValid ? 'text-green-600' : 'text-orange-500')}>
          {tokenValid ? 'Authenticated' : 'Invalid token'}
        </span>
        <button
          onClick={handleClearToken}
          className="text-xs text-muted-foreground hover:text-destructive flex items-center"
        >
          <LogOut className="h-3 w-3 mr-1" />
          Log out
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {sessionExpired && (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-500/50 bg-amber-500/10 px-2 py-1.5">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-amber-500" />
          <p className="text-[11px] leading-snug text-amber-600 dark:text-amber-400">
            Session expired or invalid — please sign in again.
          </p>
        </div>
      )}

      <div className="flex items-center gap-1">
        <button
          onClick={() => setAuthMode('login')}
          className={cn(
            'text-xs px-2 py-0.5 rounded',
            authMode === 'login' ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-accent'
          )}
        >
          <LogIn className="h-3 w-3 inline mr-1" />
          Login
        </button>
        <button
          onClick={() => setAuthMode('token')}
          className={cn(
            'text-xs px-2 py-0.5 rounded',
            authMode === 'token' ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-accent'
          )}
        >
          <KeyRound className="h-3 w-3 inline mr-1" />
          Token
        </button>
      </div>

      {authMode === 'login' ? (
        <div className="space-y-1.5">
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username"
            className="w-full text-xs bg-background border border-border rounded px-2 py-1"
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full text-xs bg-background border border-border rounded px-2 py-1"
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          />
          <button
            onClick={handleLogin}
            disabled={loginLoading || !username || !password}
            className="w-full text-xs bg-primary text-primary-foreground rounded px-2 py-1 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loginLoading ? (
              <span className="animate-spin inline-block h-3 w-3 border-2 border-current border-t-transparent rounded-full mr-1" />
            ) : (
              <LogIn className="h-3 w-3 mr-1" />
            )}
            {loginLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </div>
      ) : (
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Paste accessToken</label>
          <input
            type="password"
            defaultValue={token || ''}
            onBlur={(e) => handleSetToken(e.target.value)}
            placeholder="Paste accessToken JWT..."
            className="w-full text-xs bg-background border border-border rounded px-2 py-1"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Copy the <b>accessToken</b> from Mission Control {ENVIRONMENT_LABEL} localStorage
            (client <b>{COGNITO_CLIENT_ID}</b>)
          </p>
        </div>
      )}
    </div>
  );
}
